// URL prompt for the rich text editor's Link button. Replaces window.prompt
// (banned by CLAUDE.md) with a proper shadcn Dialog. Empty submit unsets
// the existing link, mirroring Tiptap's editor.commands.unsetLink behaviour.

import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/Dialog'

interface LinkDialogProps {
  open: boolean
  initialUrl?: string
  initialText?: string
  onOpenChange: (open: boolean) => void
  onSubmit: (text: string, url: string) => void
}

export function LinkDialog({ open, initialUrl = '', initialText = '', onOpenChange, onSubmit }: LinkDialogProps) {
  const [text, setText] = useState(initialText)
  const [url, setUrl] = useState(initialUrl)

  useEffect(() => {
    if (open) {
      setText(initialText)
      setUrl(initialUrl)
    }
  }, [open, initialText, initialUrl])

  const editing = Boolean(initialUrl)
  const trimmedUrl = url.trim()
  const trimmedText = text.trim()
  const willRemove = editing && trimmedUrl === ''

  function submit() {
    onSubmit(trimmedText, trimmedUrl)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit link' : 'Insert link'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Update the text or URL. Clear the URL to remove the link.'
              : 'Enter the text to display and the URL it links to.'}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => { e.preventDefault(); submit() }}
          className="space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="link-text">Text</Label>
            <Input
              id="link-text"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Link text"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="link-url">URL</Label>
            <Input
              id="link-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com"
              type="url"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!willRemove && trimmedUrl === ''}>
              {willRemove ? 'Remove link' : editing ? 'Update' : 'Insert'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
